import { Down, Up } from "@repo/icons";
import { useEffect, useRef, useState, type JSX } from "react";
import { cn } from "@repo/lib";

export interface AccordionProps {
  title: string;
  defaultOpen?: boolean;
  openEvent?: () => void;
  closeEvent?: () => void;
  children: string | JSX.Element | JSX.Element[];
  hasDivider?: boolean;
  defaultTextWhenClosed?: string;
}

/**
 * @param title - O título a ser exibido para a seção.
 * @param defaultOpen - Se o accordion deve ser aberto por padrão.
 * @param openEvent - Callback quando o accordion é aberto.
 * @param closeEvent - Callback quando o accordion é fechado.
 * @param children - Conteúdo a ser renderizado dentro da seção do accordion.
 * @param hasDivider - Se o accordion deve ter um divisor.
 * @param defaultTextWhenClosed - Texto a ser exibido quando o accordion está fechado.
 */
export const OnelivAccordion = ({
  title,
  children,
  defaultOpen = false,
  openEvent,
  closeEvent,
  hasDivider = true,
  defaultTextWhenClosed,
}: AccordionProps): JSX.Element => {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  const [hasMounted, setHasMounted] = useState<boolean>(false);
  const accordionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const accordionDiv = accordionRef.current;
    const contentDiv = accordionDiv?.firstElementChild as HTMLDivElement | null;

    if (!accordionDiv || !contentDiv) {
      return;
    }

    const observer = new ResizeObserver(() => {
      if (open) {
        accordionDiv.style.height = `${contentDiv.scrollHeight}px`;
      } else {
        accordionDiv.style.height = "0";
      }
    });

    observer.observe(contentDiv);

    return () => observer.disconnect();
  }, [open]);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const onAccordionClick = (): void => {
    if (!open && openEvent) openEvent();
    if (open && closeEvent) closeEvent();
    setOpen(!open);
  };

  return (
    <div className={cn("pb-1", hasDivider ? "border-b border-neutral-200" : "")}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions --- esse aqui precisa ser uma div para o caso do
      usuario clicar em qualquer parte do header do accordion, já temos um botão acessivel via teclado mais abaixo*/}
      <div
        className="flex cursor-pointer items-start justify-between"
        onClick={onAccordionClick}
        data-testid="accordion-toggle"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onAccordionClick();
          }
        }}
      >
        <p className="font-semibold text-neutral-500" data-testid="accordion-header">
          {title}
        </p>
        <button onClick={() => setOpen(!open)} type="button">
          {open ? <Up size={24} /> : <Down size={24} />}
        </button>
      </div>
      <div
        className={cn(
          "overflow-hidden",
          hasMounted && "transition-[height] duration-500",
        )}
        data-testid="accordion-content"
        aria-hidden={!open}
        ref={accordionRef}
      >
        <div className="pt-3">{children}</div>
      </div>

      {defaultTextWhenClosed && !open ? (
        <p className="text-neutral-500 text-sm">{defaultTextWhenClosed}</p>
      ) : null}
    </div>
  );
};
